import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const MessagingContext = createContext();

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

export const MessagingProvider = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);

  // Load conversations and messages from localStorage
  useEffect(() => {
    const storedConversations = JSON.parse(localStorage.getItem('conversations') || '[]');
    const storedMessages = JSON.parse(localStorage.getItem('messages') || '[]');
    setConversations(storedConversations);
    setMessages(storedMessages);
  }, []);

  // Save to localStorage whenever conversations or messages change
  useEffect(() => {
    localStorage.setItem('conversations', JSON.stringify(conversations));
  }, [conversations]);

  useEffect(() => {
    localStorage.setItem('messages', JSON.stringify(messages));
  }, [messages]);

  // Get conversations for current user
  const getUserConversations = () => {
    if (!user) return [];
    
    return conversations.filter(conv => 
      conv.participants.includes(user.id)
    ).map(conv => {
      const conversationMessages = messages.filter(msg => msg.conversationId === conv.id);
      const lastMessage = conversationMessages.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      )[0];
      
      const unreadCount = conversationMessages.filter(msg => 
        !msg.read && msg.senderId !== user.id
      ).length;

      return {
        ...conv,
        lastMessage,
        unreadCount
      };
    }).sort((a, b) => {
      const aTime = a.lastMessage ? new Date(a.lastMessage.timestamp) : new Date(0);
      const bTime = b.lastMessage ? new Date(b.lastMessage.timestamp) : new Date(0);
      return bTime - aTime;
    });
  };

  // Get messages for a conversation
  const getConversationMessages = (conversationId) => {
    return messages
      .filter(msg => msg.conversationId === conversationId)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  // Create or get conversation with a user
  const createConversation = (participantId, participantName, participantRole) => {
    if (!user) return null;

    // Check if conversation already exists
    const existingConv = conversations.find(conv => 
      conv.participants.includes(user.id) && 
      conv.participants.includes(participantId)
    );

    if (existingConv) {
      setActiveConversation(existingConv.id);
      return existingConv;
    }

    // Create new conversation
    const newConversation = {
      id: `conv_${Date.now()}`,
      participants: [user.id, participantId],
      participantNames: {
        [user.id]: user.name,
        [participantId]: participantName
      },
      participantRoles: {
        [user.id]: user.role,
        [participantId]: participantRole
      },
      createdAt: new Date().toISOString()
    };

    setConversations([...conversations, newConversation]);
    setActiveConversation(newConversation.id);
    return newConversation;
  };

  // Send a message
  const sendMessage = (conversationId, content, type = 'text') => {
    if (!user || !content.trim()) return;

    const newMessage = {
      id: `msg_${Date.now()}`,
      conversationId,
      senderId: user.id,
      senderName: user.name,
      content,
      type, // text, image, file
      timestamp: new Date().toISOString(),
      read: false
    };

    setMessages([...messages, newMessage]);
    return newMessage;
  };

  // Mark messages as read
  const markAsRead = (conversationId) => {
    if (!user) return;

    setMessages(prevMessages => 
      prevMessages.map(msg => 
        msg.conversationId === conversationId && msg.senderId !== user.id
          ? { ...msg, read: true }
          : msg
      )
    );
  };

  // Delete a conversation
  const deleteConversation = (conversationId) => {
    setConversations(prevConversations => 
      prevConversations.filter(conv => conv.id !== conversationId)
    );
    setMessages(prevMessages => 
      prevMessages.filter(msg => msg.conversationId !== conversationId)
    );
    if (activeConversation === conversationId) {
      setActiveConversation(null);
    }
  };

  // Start a call (stores call intent)
  const initiateCall = (conversationId, callType) => {
    // In a real app, this would integrate with WebRTC or a video calling service
    // For now, we'll just add a system message
    const conversation = conversations.find(conv => conv.id === conversationId);
    if (!conversation) return;

    const callMessage = {
      id: `msg_${Date.now()}`,
      conversationId,
      senderId: 'system',
      senderName: 'System',
      content: `${user.name} initiated a ${callType} call`,
      type: 'call',
      callType, // 'video' or 'audio'
      timestamp: new Date().toISOString(),
      read: false
    };

    setMessages([...messages, callMessage]);
    return callMessage;
  };

  const value = {
    conversations: getUserConversations(),
    activeConversation,
    setActiveConversation,
    messages: activeConversation ? getConversationMessages(activeConversation) : [],
    createConversation,
    sendMessage,
    markAsRead,
    deleteConversation,
    initiateCall
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};
